import React, { useState, useEffect, useRef, useCallback, useId } from "react";
import { ChevronDown, Check } from "lucide-react";

const CustomSelect = ({ value, onChange, options, icon: Icon, label, className = "", disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const ref = useRef(null);
  const listRef = useRef(null);
  const instanceId = useId();
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Reset focused index when opening
  useEffect(() => {
    if (open) {
      const idx = options.findIndex((o) => o.value === value);
      setFocusedIndex(idx >= 0 ? idx : 0);
    }
  }, [open, options, value]);

  const handleKeyDown = useCallback((e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((prev) => Math.min(prev + 1, options.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < options.length) {
          onChange(options[focusedIndex].value);
          setOpen(false);
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        break;
      default:
        break;
    }
  }, [open, focusedIndex, options, onChange]);

  // Scroll focused option into view
  useEffect(() => {
    if (open && listRef.current && focusedIndex >= 0) {
      const item = listRef.current.children[focusedIndex];
      if (item) item.scrollIntoView({ block: "nearest" });
    }
  }, [open, focusedIndex]);

  return (
    <div ref={ref} className={className}>
      {label && (
        <label
          className="block text-xs font-medium mb-1.5 uppercase tracking-wide"
          style={{ color: "var(--color-text-muted)" }}
        >
          {label}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setOpen(!open)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="field-input w-full flex items-center justify-between text-left disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ paddingRight: "2rem", paddingLeft: Icon ? "2.25rem" : undefined }}
        >
          {Icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <Icon size={15} style={{ color: "var(--color-mint)" }} />
            </div>
          )}
          <span className="truncate text-[var(--color-text-primary)]">
            {selected?.label || value}
          </span>
          <ChevronDown
            className="h-4 w-4 absolute right-2 top-1/2 -translate-y-1/2 transition-transform"
            style={{
              color: "var(--color-mint)",
              transform: open ? "translateY(-50%) rotate(180deg)" : "translateY(-50%)",
            }}
          />
        </button>
        {open && (
          <div
            ref={listRef}
            role="listbox"
            tabIndex={-1}
            aria-activedescendant={focusedIndex >= 0 ? `${instanceId}-opt-${focusedIndex}` : undefined}
            className="absolute z-50 mt-1 w-full rounded-lg border border-[var(--color-border)] overflow-hidden max-h-60 overflow-y-auto"
            style={{
              backgroundColor: "var(--color-surface)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            }}
          >
            {options.map((opt, idx) => (
              <button
                key={opt.value}
                id={`${instanceId}-opt-${idx}`}
                type="button"
                role="option"
                aria-selected={value === opt.value}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                onMouseEnter={() => setFocusedIndex(idx)}
                className="w-full px-3 py-2 text-sm text-left flex items-center justify-between transition-colors"
                style={{
                  backgroundColor:
                    focusedIndex === idx
                      ? "var(--color-surface-raised)"
                      : value === opt.value
                        ? "var(--color-mint-muted)"
                        : "transparent",
                  color: value === opt.value ? "var(--color-mint)" : "var(--color-text-primary)",
                }}
              >
                <span>{opt.label}</span>
                {value === opt.value && <Check className="h-3.5 w-3.5" style={{ color: "var(--color-mint)" }} />}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomSelect;
